import {useMemo} from 'react';
import {kr, tickLabels} from '../../lib/format.js';
import {Defs, fillById, niceScale, Grid, useHover, HitBands, Tooltip, Legend} from './primitives.jsx';

const W = 560, H = 300, L = 60, R = 10, T = 10, B = 24;

/**
 * Revenue up, operating cost down, booked months beside projected ones.
 *
 * The projected bars are OPEN (white, outlined) where booked bars are solid, so the two
 * are separable at a glance and in greyscale print — this palette has no hue to spend on
 * the distinction (rule 14). The partly-booked month is grey, exactly as it is on the
 * dashboard, because it is neither a full outcome nor a projection.
 *
 * The flat forward bars are the point, not a defect: the projection assumes no growth,
 * so the run-rate is a horizontal line by construction. A reader who sees it rising
 * would be reading in an assumption nobody made.
 */
export default function RevenueForecast({rows}) {
	const {at, enter, leave} = useHover();
	const ticks = useMemo(() => tickLabels(rows), [rows]);
	const g = useMemo(() => {
		const hi = Math.max(0, ...rows.map(r => r.revenue ?? 0));
		const lo = -Math.max(0, ...rows.map(r => r.operating_cost ?? 0));
		const sc = niceScale(lo, hi);
		const iw = W - L - R, ih = H - T - B;
		return {
			sc,
			y: v => T + ih - ((v - sc.lo) / (sc.hi - sc.lo)) * ih,
			x: i => L + (iw * (i + 0.5)) / rows.length,
			bw: Math.max(2, Math.min(22, (iw / rows.length) * 0.7)),
		};
	}, [rows]);

	if (!rows.length) return null;

	// Booked, partly booked and projected are three states, not two. Folding the partial
	// month into either neighbour is the mistake rule 4 exists to prevent.
	const paintFor = r => (r.projected ? fillById('open') : r.ledger_partial ? fillById('light') : fillById('solid'));
	const costPaint = r => (r.projected ? fillById('open') : r.ledger_partial ? fillById('light') : fillById('hatch'));

	const bar = (r, i, value, paint, key) => {
		if (!value) return null;
		const zero = g.y(0), yv = g.y(value);
		return <rect key={key} x={g.x(i) - g.bw / 2} y={Math.min(zero, yv)} width={g.bw}
			height={Math.max(Math.abs(yv - zero), 0.6)} rx={g.bw > 6 ? 2 : 0} {...paint} />;
	};

	return (
		<div className="chart">
			<div className="chart__head">
				<span className="chart__title">Intäkt och rörelsekostnad, bokfört och framskrivet</span>
				<span className="chart__hint">
					intäkt uppåt, rörelsekostnad nedåt. Framskrivna månader är ofyllda — de är
					snittet av tre helt bokförda månader, inte ett utfall och inte ett mål.
				</span>
			</div>
			<div className="chart__canvas">
				<svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet" role="img"
					aria-label="Intäkt och rörelsekostnad per månad, bokfört och framskrivet">
					<Defs />
					<Grid {...g.sc} y={g.y} L={L} R={R} W={W} />
					{rows.map((r, i) => bar(r, i, r.revenue, paintFor(r), `rev-${r.month}`))}
					{rows.map((r, i) => bar(r, i, -(r.operating_cost ?? 0), costPaint(r), `cost-${r.month}`))}
					<line x1={L} x2={W - R} y1={g.y(0)} y2={g.y(0)} stroke="#000" strokeWidth={1} />
					{ticks.map((t, i) => (t == null ? null : (
						<text key={rows[i].month} x={g.x(i)} y={H - 8} className="axs" textAnchor="middle">{t}</text>
					)))}
					<HitBands months={rows} x={g.x} W={W} H={H} onEnter={enter} onLeave={leave} />
				</svg>
				<Tooltip months={rows} at={at} x={g.x} W={W}
					title={m => `${m.label ?? m.month}${m.projected ? ' · prognos' : ''}`}
					rows={m => [
						['Intäkt', `${kr(m.revenue)} kr`],
						['Rörelsekostnad', `−${kr(m.operating_cost)} kr`],
						['Resultat', `${kr(m.result)} kr`],
					]} />
			</div>
			<Legend items={[
				{label: 'Bokförd intäkt', fill: 'solid'},
				{label: 'Bokförd rörelsekostnad', fill: 'hatch'},
				{label: 'Delvis bokförd månad', fill: 'light'},
				{label: 'Framskrivet', fill: 'open'},
			]} />
		</div>
	);
}
